<template>
    <div v-if="pending">
        <div class="d-flex justify-content-center loading-center">
            <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>
    </div>
	<div v-else class="container py-5">
        <h2>{{ $t('performanceDetail') }}</h2>
        <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
                <li class="breadcrumb-item">
                    <RouterLink to="/modelings">
                        {{ $t('home') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="{ name: 'modeling', params: { id: this.$route.params.id } }">
                        {{ modelingName || $t('modelings') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <a class="link" style="cursor: pointer" @click="$router.go(-1)">
                        {{ assetTypeName || $t('assetType') }}
                    </a>
                </li>
                <li class="breadcrumb-item">{{ data.name }}</li>
            </ol>
        </nav>
        <div class="row mb-4">
            <div class="col-md-4">
                <label class="form-label" for="performanceName" required>{{ $t('performanceName') }}</label>
                <input v-model="data.name" type="text" class="form-control" id="performanceName" >
            </div>
              <div class="col-md-4">
                <label class="form-label" for="performanceDescription" required>{{ $t('performanceDescription') }}</label>
                <input v-model="data.description" type="text" class="form-control" id="performanceDescription">
            </div>
                <div class="col-md-4">
                <label class="form-label" for="performanceLifespan" required>{{ $t('performanceLifespan') }}</label>
                <input v-model="data.lifespan" type="text" class="form-control" id="performanceLifespan">
            </div>
        </div>
        <button class="fa fa-eye btn btn-outline-primary float-end" @click="showTableFunction"></button>
        <div class="mb-4" v-if="showTable">
            <h4>{{ $t('performanceRanges') }}</h4>
            <table class="table">
                <thead>
                    <tr>
                        <th scope="col"></th>
                        <th scope="col">{{ $t('startTheoreticalAge') }}</th>
                        <th scope="col">{{ $t('endTheoreticalAge') }}</th>
                        <th scope="col">{{ $t('startAssetPerformance') }}</th>
                        <th scope="col">{{ $t('endAssetPerformance') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(performanceRange, i) in data.performanceRanges" class="align-middle"  :key="i">
                        <th scope="row"><i class="fa fa-remove" @click="removePerformanceRange(i)"></i></th>
                        <td>
                            <input v-model="performanceRange.startTheoreticalAge" disabled type="number" class="form-control" :id="'startTheoreticalAge_' + i">
                        </td>
                        <td>
                            <input v-model="performanceRange.endTheoreticalAge" :max="data.lifespan" @input="updateTheoreticalAgeStart(performanceRange.endTheoreticalAge, i)" type="number" class="form-control" :id="'endTheoreticalAge_' + i">
                        </td>
                        <td>
                            <input v-model="performanceRange.startAssetPerformance" :disabled="i > 0" type="number" class="form-control" :id="'startAssetPerformance_' + i">
                        </td>
                        <td>
                            <input v-model="performanceRange.endAssetPerformance" @input="updateAssetPerformanceStart(performanceRange.endAssetPerformance, i)"  type="number" class="form-control" :id="'endAssetPerformance_' + i">
                        </td>
                    </tr>
                </tbody>
            </table>
            <button class="btn btn-secondary w-25 mx-auto d-block" @click="addPerformanceRange"><i class="fa-solid fa-plus"></i></button>
        </div>
        <div v-else class="mb-4">
            <h4>{{ $t('performanceRangesChart') }}</h4>
            <apexchart class="d-flex justify-content-center" type="line" width="800px" :options="chartOptions" :series="series"></apexchart>
        </div>
        <div class="d-grid gap-2 d-md-flex">
            <template v-if="isUserAdmin">
                <button type="button" class="btn btn-primary" @click="onSubmit">{{ $t('save') }}</button>
                <button type="button" class="btn btn-danger"  @click="showDeletePerformance">{{ $t('delete') }}</button>
            </template>
            <button type="button" class="btn btn-secondary" @click="$router.push({ path: `/modeling/${this.$route.params.id}/assettype/${this.$route.params.assetTypeId}` })">{{ $t('back') }}</button>
        </div>
        <PerformanceDeleteModal v-if="showPerformanceDeleteModal" @reload="$router.push({ path: `/modeling/${this.$route.params.id}/assettype/${this.$route.params.assetTypeId}` })" @closeDeleteDialog="showPerformanceDeleteModal = false"/>
    </div>
</template>

<script>
import ModelingApi from "../../api/server/modeling";
import AssetTypeApi from "../../api/server/assetType";
import PerformanceApi from "../../api/server/performance";
import PerformanceDeleteModal from '../performances/PerformanceDeleteModal.vue';
import { CheckIfUserIsEditor } from '../../authCache';

export default {
    name: "performancedetail",
    watch: {
        $route: "requestData",
    },
    components: {
        PerformanceDeleteModal
    },
    mounted() {
        this.requestData();
    },
    methods: {
        showTableFunction() {
            this.generateGraph();
            this.showTable = !this.showTable;
        },
        updateTheoreticalAgeStart(value, i) {
            var range = this.data.performanceRanges[i+1];
            if(range) {
                range.startTheoreticalAge = value;
            }

            // Pokud je hodnota vyšší než lifespan, nastavíme lifespan
            if(value > this.data.lifespan) {
                this.data.performanceRanges[i].endTheoreticalAge = this.data.lifespan;
            }
        },
        updateAssetPerformanceStart(value, i) {
            var range = this.data.performanceRanges[i+1];
            if(range) {
                range.startAssetPerformance = value;
            }
        },
        generateGraph() {
            let seriesData = [];

            for (let performance of this.data.performanceRanges) {
                seriesData.push({ x: performance.startTheoreticalAge, y: performance.startAssetPerformance });
                seriesData.push({ x: performance.endTheoreticalAge, y: performance.endAssetPerformance });
            }
            
            this.series = [{
                name: "Asset Performance",
                data: seriesData
            }];
        },
        async requestData() {
            this.pending = true;
            this.isUserAdmin = await CheckIfUserIsEditor();

            var modelingInfo = await ModelingApi.modelingInfo(this.$route.params.id);
            var foundAssetType = modelingInfo.assetTypes.find(a => a.id == this.$route.params.assetTypeId);
            if(foundAssetType != null) {
                for(var i in foundAssetType.lifeCycles) {
                    var lifeCycle = foundAssetType.lifeCycles[i];
                    var performance = lifeCycle.performance;
                    if(performance && performance.id == this.$route.params.performanceId) {
                        this.foundPerformances.push({ 
                            lifeCycleId: lifeCycle.id,
                            performanceId: performance.performanceId,
                            name: "LifeCycle - " + lifeCycle.name + " - " + performance.name
                        });
                    }
                }
            }

            this.data = await PerformanceApi.performanceDetail(this.$route.params.id, this.$route.params.performanceId);
           
            var assetType = await AssetTypeApi.assetTypeDetail(this.$route.params.id, this.$route.params.assetTypeId);
            this.assetTypeName = assetType.name;

            var modeling = await ModelingApi.modelingDetail(this.$route.params.id);
            this.modelingName = modeling.name;

            this.pending = false;
        },
        removePerformanceRange(rowIndex) {
            this.data.performanceRanges.splice(rowIndex, 1);
        },
        addPerformanceRange() {
            this.data.performanceRanges.push({  
                description: "",
                startTheoreticalAge: this.getMinStartTheoreticalAge(),
                endTheoreticalAge: 0,
                startAssetPerformance: this.getMinStartAssetPerformance(),
                endAssetPerformance: 0.0
            })
        },
        async onSubmit() {
            if(this.data.name.length > 0) {
                const max = Math.max(...this.data.performanceRanges.map(o => o.endTheoreticalAge));
                if(max > this.data.lifespan) {
                    this.showLifespanError();
                } else {
                    await PerformanceApi.performanceUpdate(this.$route.params.id, this.data);
                    this.showSuccess();
                }
            } else {
                this.showEmptyNameError();
            }
        },
        getMinStartTheoreticalAge() {
            var minStartTheoreticalAge = 0;
            if(this.data.performanceRanges.length > 0)
                minStartTheoreticalAge = this.data.performanceRanges[this.data.performanceRanges.length -1].endTheoreticalAge;

            return minStartTheoreticalAge;
        },
        getMinStartAssetPerformance() {
            var minStartAssetPerformance = 0;
            if(this.data.performanceRanges.length > 0)
                minStartAssetPerformance = this.data.performanceRanges[this.data.performanceRanges.length -1].endAssetPerformance;

            return minStartAssetPerformance;
        },
        showDeletePerformance() {
            this.performanceToDeleteId = this.data.id;
            this.showPerformanceDeleteModal = true;
        }
    },
    notifications: {
        showSuccess: { 
            title: 'success',
            message: 'performanceUpdated',
            type: 'success'
        },
        showLifespanError: { 
            title: 'error',
            message: 'lifespanError',
            type: 'error'
        },
        showEmptyNameError: { 
            title: 'error',
            message: 'emptyName',
            type: 'error'
        }
    },
    data() {
        return {
            data: {
                name: "",
                id: "",
                performance: {},
                performanceRanges: []
            },
            performanceToDeleteId: 0,
            showPerformanceDeleteModal: false,
            modelingName: null,
            assetTypeName: null,
            pending: true,
            showTable: true,
            chartOptions: {
                chart: {
                    type: 'line',
                    animations: {
                        enabled: false
                    },
                    height: 350,
                    toolbar: {
                        show: false
                    }
                },
                xaxis: {
                    title: {
                        text: 'Theoretical Age',
                    },
                    categories: []
                },
                yaxis: {
                    title: {
                        text: 'Asset Performance',
                    },
                },
            },
            series: [],
            foundPerformances: [],
            isUserAdmin: false
        }
    }
};
</script>